import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */

export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`There are two ways to implement feature flags in React Native:`}</p>
    <ol>
      <li parentName="ol">{`Using hooks.`}</li>
      <li parentName="ol">{`Loading the flag directly.`}</li>
    </ol>
    <h3 {...{
      "id": "method-1-using-hooks"
    }}>{`Method 1: Using hooks`}</h3>
    <h4 {...{
      "id": "example-1-boolean-feature-flags"
    }}>{`Example 1: Boolean feature flags`}</h4>
    <pre><code parentName="pre" {...{
        "className": "language-react-native"
      }}>{`import { useFeatureFlag } from 'posthog-react-native'

const MyComponent = () => {
    const booleanFlag = useFeatureFlag('key-for-your-boolean-flag')

    if (booleanFlag === undefined) {
        // the response is undefined if the flags are being loaded
        return null
    }

    // Optional use the 'useFeatureFlagWithPayload' hook for fetching the feature flag payload

    return booleanFlag ? <Text>Testing feature 😄</Text> : <Text>Not Testing feature 😢</Text>
}
`}</code></pre>
    <h4 {...{
      "id": "example-2-multivariate-feature-flags"
    }}>{`Example 2: Multivariate feature flags`}</h4>
    <pre><code parentName="pre" {...{
        "className": "language-react-native"
      }}>{`import { useFeatureFlag } from 'posthog-react-native'

const MyComponent = () => {
    const multiVariantFeature = useFeatureFlag('key-for-your-multivariate-flag')

    if (multiVariantFeature === undefined) {
        // the response is undefined if the flags are being loaded
        return null
    } else if (multiVariantFeature === 'variant-name') { // replace 'variant-name' with the name of your variant
      // Do something
    }

    // Optional use the 'useFeatureFlagWithPayload' hook for fetching the feature flag payload

    return <div/>
}
`}</code></pre>
    <h3 {...{
      "id": "method-2-loading-the-flag-directly"
    }}>{`Method 2: Loading the flag directly`}</h3>
    <pre><code parentName="pre" {...{
        "className": "language-react-native"
      }}>{`// Defaults to undefined if not loaded yet or if there was a problem loading
posthog.isFeatureEnabled('key-for-your-boolean-flag')

// Defaults to undefined if not loaded yet or if there was a problem loading
posthog.getFeatureFlag('key-for-your-boolean-flag')

// Multivariant feature flags are returned as a string
posthog.getFeatureFlag('key-for-your-multivariate-flag')

// Optional fetch the payload returns 'JsonType' or undefined if not loaded yet or if there was a problem loading
posthog.getFeatureFlagPayload('key-for-your-multivariate-flag')
`}</code></pre>
    <h3 {...{
      "id": "reloading-flags"
    }}>{`Reloading flags`}</h3>
    <p>{`PostHog loads feature flags when instantiated and refreshes whenever methods are called that affect the flag.`}</p>
    <p>{`If want to manually trigger a refresh, you can call `}<inlineCode parentName="p">{`reloadFeatureFlagsAsync()`}</inlineCode>{`:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-react-native"
      }}>{`posthog.reloadFeatureFlagsAsync().then((refreshedFlags) => console.log(refreshedFlags))
`}</code></pre>
    <p>{`Or when you want to trigger the reload, but don't care about the result:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-react-native"
      }}>{`posthog.reloadFeatureFlags()
`}</code></pre>
    <h3 {...{
      "id": "feature-flag-caching"
    }}>{`Feature flag caching`}</h3>
    <p>{`The React Native SDK caches feature flag values in AsyncStorage. Cached values persist indefinitely with no TTL until updated by a successful API call. This enables offline support and reduces latency, but means `}<strong parentName="p">{`inactive users may see stale flag values`}</strong>{` from their last session.`}</p>
    <p>{`For example, if a user last opened your app when a flag was `}<inlineCode parentName="p">{`false`}</inlineCode>{`, that value remains cached even after you roll it out to 100%. When they reopen the app, the SDK returns the cached `}<inlineCode parentName="p">{`false`}</inlineCode>{` first, then fetches the fresh `}<inlineCode parentName="p">{`true`}</inlineCode>{` value from the API.`}</p>
    <p>{`To ensure fresh flag values:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-react-native"
      }}>{`// Force refresh on app start
await posthog.reloadFeatureFlagsAsync()
`}</code></pre>
    <p>{`Or clear cached values for inactive users:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-react-native"
      }}>{`if (lastActiveDate < migrationDate) {
  posthog.reset() // Clears all cached data
}
`}</code></pre>
    <h3 {...{
      "id": "request-timeout"
    }}>{`Request timeout`}</h3>
    <p>{`You can configure the `}<inlineCode parentName="p">{`featureFlagsRequestTimeoutMs`}</inlineCode>{` parameter when initializing your PostHog client to set a flag request timeout. This helps prevent your code from being blocked in the case when PostHog's servers are too slow to respond. By default, this is set at 10 seconds.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-react-native"
      }}>{`export const posthog = new PostHog('<ph_project_api_key>', {
  // usually 'https://us.i.posthog.com' or 'https://eu.i.posthog.com'  
  host: '<ph_client_api_host>',
  featureFlagsRequestTimeoutMs: 10000 // Time in milliseconds. Default is 10000 (10 seconds).
})
`}</code></pre>
    <h3 {...{
      "id": "error-handling"
    }}>{`Error handling`}</h3>
    <p>{`When using the PostHog SDK, it's important to handle potential errors that may occur during feature flag operations. Here's an example of how to wrap PostHog SDK methods in an error handler:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-react-native"
      }}>{`function handleFeatureFlag(client, flagKey, distinctId) {
    try {
        const isEnabled = client.isFeatureEnabled(flagKey, distinctId);
        console.log(\`Feature flag '\${flagKey}' for user '\${distinctId}' is \${isEnabled ? 'enabled' : 'disabled'}\`);
        return isEnabled;
    } catch (error) {
        console.error(\`Error fetching feature flag '\${flagKey}': \${error.message}\`);
        // Optionally, you can return a default value or throw the error
        // return false; // Default to disabled
        throw error;
    }
}

// Usage example
try {
    const flagEnabled = handleFeatureFlag(client, 'new-feature', 'user-123');
    if (flagEnabled) {
        // Implement new feature logic
    } else {
        // Implement old feature logic
    }
} catch (error) {
    // Handle the error at a higher level
    console.error('Feature flag check failed, using default behavior');
    // Implement fallback logic
}
`}</code></pre>
    <h3 {...{
      "id": "overriding-server-properties"
    }}>{`Overriding server properties`}</h3>
    <p>{`Sometimes, you might want to evaluate feature flags using properties that haven't been ingested yet, or were set incorrectly earlier. You can do so by setting properties the flag depends on with these calls:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-react-native"
      }}>{`posthog.setPersonPropertiesForFlags({'property1': 'value', property2: 'value2'})
`}</code></pre>
    <p>{`Note that these are set for the entire session. Successive calls are additive: all properties you set are combined together and sent for flag evaluation.`}</p>
    <p>{`Whenever you set these properties, we also trigger a reload of feature flags to ensure we have the latest values. You can disable this by passing in the optional parameter for reloading:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-react-native"
      }}>{`posthog.setPersonPropertiesForFlags({'property1': 'value', property2: 'value2'}, false)
`}</code></pre>
    <p>{`At any point, you can reset these properties by calling `}<inlineCode parentName="p">{`resetPersonPropertiesForFlags`}</inlineCode>{`:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-react-native"
      }}>{`posthog.resetPersonPropertiesForFlags()
`}</code></pre>
    <p>{`The same holds for `}<a parentName="p" {...{
        "href": "/docs/product-analytics/group-analytics"
      }}>{`group`}</a>{` properties:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-react-native"
      }}>{`// set properties for a group
posthog.setGroupPropertiesForFlags({'company': {'property1': 'value', property2: 'value2'}})

// reset properties for all groups:
posthog.resetGroupPropertiesForFlags()
`}</code></pre>
    <blockquote>
      <p parentName="blockquote"><strong parentName="p">{`Note:`}</strong>{` You don't need to add the group names here, since these properties are automatically attached to the current group (set via `}<inlineCode parentName="p">{`posthog.group()`}</inlineCode>{`). When you change the group, these properties are reset.`}</p>
    </blockquote>
    <p><strong parentName="p">{`Automatic overrides`}</strong></p>
    <p>{`Whenever you call `}<inlineCode parentName="p">{`posthog.identify`}</inlineCode>{` with person properties, we automatically add these properties to flag evaluation calls to help determine the correct flag values. The same is true for when you call `}<inlineCode parentName="p">{`posthog.group()`}</inlineCode>{`.`}</p>
    <p><strong parentName="p">{`Default overridden properties`}</strong></p>
    <p>{`By default, we always override some properties based on the user IP address.`}</p>
    <p>{`The list of properties that this overrides:`}</p>
    <ol>
      <li parentName="ol">{`$geoip_city_name`}</li>
      <li parentName="ol">{`$geoip_country_name`}</li>
      <li parentName="ol">{`$geoip_country_code`}</li>
      <li parentName="ol">{`$geoip_continent_name`}</li>
      <li parentName="ol">{`$geoip_continent_code`}</li>
      <li parentName="ol">{`$geoip_postal_code`}</li>
      <li parentName="ol">{`$geoip_time_zone`}</li>
    </ol>
    <p>{`This enables any geolocation-based flags to work without manually setting these properties.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      